import React, { useState } from 'react';
import styled from 'styled-components';
import { Field, useField } from 'react-final-form';

import { Checkbox, SelectInput, numberToCurrency } from 'vqp-common';
import { Input, FileInput, IconButton, CurrencyInput } from '../../common';
import { isRequired } from '../../../utils/validations';
import { PRINT_LOCATIONS } from '../../../constants/product';

import defaultImage from '../../../assets/add_image.svg';

const Wrapper = styled.div`
  padding: 0 20px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GridArea = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 11px !important;
  color: ${(props) => props.theme.colors.textBold} !important;
  min-width: ${(props) => props.minWidth || 'auto'};
  width: ${(props) => props.width || '100%'};
`;

const ImageTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Label = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.colors.textBold};
  margin-bottom: 7px;
`;

const ImageWrapper = styled.div`
  width: 215px;
  height: 215px;
  border: 1px solid ${(props) => props.theme.colors.mediumGrey};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 12px
  margin-top: -10px;
  `;

const ProductImage = styled.img`
  max-width: 215px;
  max-height: 215px;
`;

const CheckboxWrapper = styled.div`
  font-size: 11px !important;
  min-width: 225px;
  width: ${(props) => props.width || '100%'};
  margin-top: ${(props) => (props.removeMarginTop ? '-8px' : '8px')};
`;

const SaleChannelsBlock = styled.div`
  display: flex;
  flex-direction: row;
  & > * {
    padding-right: 10px;
  }
`;

const PrintLocationBlock = styled.div`
  min-height: 35px;
  display: flex;
  flex-direction: column;
`;

const ErrorBox = styled.div`
  font-size: 10px;
  color: ${(props) => props.theme.colors.error};
`;

export const ProductInformation = ({ defaultProduct }) => {
  const product = defaultProduct || {};
  const isNewProduct = !defaultProduct;

  const nameField = useField('name', { initialValue: product.name, validate: isRequired });
  const printNameEqualsField = useField('printNameEquals', {
    initialValue: (product.name && product.name === product.printName) || !product.printName
  });

  const priceField = useField('price', {
    initialValue: numberToCurrency(product.price),
    validate: isRequired
  });
  const deliveryPriceEqualsField = useField('deliveryPriceEquals', {
    initialValue:
      (product.price && product.price === product.deliveryPrice) || !product.deliveryPrice
  });

  const saleField = useField('promo', {
    initialValue: product.promo ? numberToCurrency(product.promo) : undefined
  });

  const printLocationField = useField('printLocation', {
    initialValue: product.printLocation || PRINT_LOCATIONS.KITCHEN.value,
    validate: isRequired
  });

  const imageField = useField('image', {
    initialValue: null /* , validate: isNewProduct ? isRequired : undefined */
  });
  const deleteImageField = useField('deleteImage', { initialValue: false });
  const [imageSrc, setImageSrc] = useState(product.imageUrl);

  const printLocationOptions = Object.values(PRINT_LOCATIONS);
  const handlePrintLocationSelect = (index) => {
    if (index >= 0) {
      printLocationField.input.onChange(printLocationOptions[index].value);
    } else {
      printLocationField.input.onChange(null);
    }
    printLocationField.input.onBlur();
  };

  const handleChangeImage = ({ target }) => {
    const [file] = target.files;
    if (file) {
      imageField.input.onChange(file);
      imageField.input.onBlur();
      deleteImageField.input.onChange(false);

      const reader = new FileReader();
      reader.onload = function (e) {
        setImageSrc(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    deleteImageField.input.onChange(true);
    imageField.input.onChange(null);
    setImageSrc(defaultImage);
  };

  return (
    <Wrapper>
      <Grid>
        <GridArea>
          <FormGroup width="215px">
            <ImageTitle>
              <Label>Foto</Label>
              {!isNewProduct && (
                <IconButton
                  icon="delete"
                  onClick={handleDeleteImage}
                  title={`Excluir`}
                />
              )}
            </ImageTitle>
            <label htmlFor="imageInput">
              <ImageWrapper>
                <ProductImage src={imageSrc || defaultImage} />
              </ImageWrapper>
              <FileInput
                id="imageInput"
                onChange={handleChangeImage}
              />
              <ErrorBox>{imageField.meta.touched && imageField.meta.error}</ErrorBox>
            </label>
          </FormGroup>
          <GridArea style={{ flex: 1 }}>
            <FormGroup>
              <Label>Código</Label>
              <Field
                name="code"
                validate={isRequired}
                initialValue={product.code}
              >
                {({ input, meta }) => {
                  const error = meta.touched && meta.error;
                  return (
                    <Input
                      {...input}
                      error={error}
                    />
                  );
                }}
              </Field>
            </FormGroup>
            <FormGroup width="100%">
              <Label>Nome do produto</Label>
              <Input
                {...nameField.input}
                error={nameField.meta.touched && nameField.meta.error}
              />
              <CheckboxWrapper
                width="45%"
                removeMarginTop
              >
                <Checkbox
                  checked={printNameEqualsField.input.value}
                  onCheck={printNameEqualsField.input.onChange}
                >
                  Igualar o nome
                </Checkbox>
              </CheckboxWrapper>
            </FormGroup>
            <FormGroup width="100%">
              <Label>Nome de impressão</Label>
              {printNameEqualsField.input.value === true ? (
                <Input
                  {...nameField.input}
                  disabled={true}
                />
              ) : (
                <Field
                  name="printName"
                  validate={isRequired}
                  initialValue={product.printName}
                >
                  {({ input, meta }) => {
                    const error = meta.touched && meta.error;
                    return (
                      <Input
                        {...input}
                        error={error}
                      />
                    );
                  }}
                </Field>
              )}
            </FormGroup>
          </GridArea>
        </GridArea>
        {/* <GridArea>
          <FormGroup>
            <ImageTitle>
              <Label>
                Imagem Prêmio
              </Label>
              {!isNewProduct && (
                <IconButton icon="delete" onClick={handleDeleteRewardImage} title={`Excluir`} />
              )}
            </ImageTitle>
            <label htmlFor="rewardImageInput">
              <ImageWrapper>
                <ProductImage src={rewardImageSrc || defaultImage} />
              </ImageWrapper>
              <FileInput id="rewardImageInput" onChange={handleChangeRewardImage} />
              <ErrorBox>{rewardImageField.meta.touched && rewardImageField.meta.error}</ErrorBox>
            </label>
          </FormGroup>
        </GridArea> */}

        <GridArea>
          <ValueWrapper>
            <FormGroup width="calc(33.33% - 6px)">
              <Label>Valor</Label>
              <CurrencyInput
                {...priceField.input}
                error={priceField.meta.touched && priceField.meta.error}
              />
              <CheckboxWrapper>
                <Checkbox
                  checked={deliveryPriceEqualsField.input.value}
                  onCheck={deliveryPriceEqualsField.input.onChange}
                >
                  Igualar o valor
                </Checkbox>
              </CheckboxWrapper>
            </FormGroup>
            <FormGroup width="calc(33.33% - 6px)">
              <Label>Valor de delivery</Label>
              {deliveryPriceEqualsField.input.value === true ? (
                <CurrencyInput
                  {...priceField.input}
                  disabled={true}
                />
              ) : (
                <Field
                  name="deliveryValue"
                  validate={isRequired}
                  initialValue={
                    product.deliveryPrice != null ? numberToCurrency(product.deliveryPrice) : undefined
                  }
                >
                  {({ input, meta }) => {
                    const error = meta.touched && meta.error;
                    return (
                      <CurrencyInput
                        {...input}
                        error={error}
                      />
                    );
                  }}
                </Field>
              )}
            </FormGroup>

            <FormGroup width="calc(33.33% - 6px)">
              <Label>Valor promocional</Label>
              <CurrencyInput
                {...saleField.input}
                error={saleField.meta.touched && saleField.meta.error}
              />
            </FormGroup>
          </ValueWrapper>
        </GridArea>

        <GridArea>
          <FormGroup width="calc(50% - 6px)">
            <Label>Local de impressão</Label>
            <PrintLocationBlock>
              <SelectInput
                options={printLocationOptions}
                selected={printLocationOptions.findIndex(
                  (opt) => opt.value === printLocationField.input.value
                )}
                onSelect={handlePrintLocationSelect}
                placeholder={'Selecione'}
              />
              <ErrorBox>{printLocationField.meta.dirty && printLocationField.meta.error}</ErrorBox>
            </PrintLocationBlock>
          </FormGroup>
          <FormGroup width="calc(50% - 6px)">
            <Label>Canais de venda</Label>
            <SaleChannelsBlock>
              <Field
                name="delivery"
                initialValue={product.delivery}
              >
                {({ input, meta }) => (
                  <Checkbox
                    checked={input.value}
                    onCheck={input.onChange}
                  >
                    Delivery
                  </Checkbox>
                )}
              </Field>
              <Field
                name="counter"
                initialValue={product.counter}
              >
                {({ input, meta }) => (
                  <Checkbox
                    checked={input.value}
                    onCheck={input.onChange}
                  >
                    Balcão
                  </Checkbox>
                )}
              </Field>
            </SaleChannelsBlock>
          </FormGroup>
          <FormGroup width="calc(50% - 6px)">
            <Field
              name="displayFrom"
              initialValue={product.displayFrom}
            >
              {({ input, meta }) => (
                <CheckboxWrapper>
                  <Checkbox
                    checked={input.value}
                    onCheck={input.onChange}
                  >
                    Exibir 'A partir de'?
                  </Checkbox>
                </CheckboxWrapper>
              )}
            </Field>
          </FormGroup>
        </GridArea>

        {/* <GridArea>
          <FormGroup>
            <Label>
              Valor Prêmio (opcional)
            </Label>
            <CurrencyInput
              {...rewardValueField.input}
              error={rewardValueField.meta.touched && rewardValueField.meta.error}
            />
          </FormGroup>
        </GridArea> */}

        <GridArea>
          <FormGroup>
            <Label>Descrição</Label>
            <Field
              name="description"
              initialValue={product.description}
              validate={isRequired}
            >
              {({ input, meta }) => {
                const error = meta.touched && meta.error;
                return (
                  <Input
                    multi
                    rows={6}
                    {...input}
                    error={error}
                  />
                );
              }}
            </Field>
          </FormGroup>
        </GridArea>
      </Grid>
    </Wrapper>
  );
};
